import { useLayoutEffect, useRef, useState } from "react";
import { motion, useScroll, useTransform, useMotionValue, useSpring } from "framer-motion";
import { ArrowDownRight, MapPin } from "lucide-react";
import { scrollToSection } from "@/lib/content";
import { useT } from "@/i18n";

const HERO_IMG =
  "https://images.unsplash.com/photo-1714601344981-75e003bc5d18?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1ODR8MHwxfHNlYXJjaHwzfHx3YXJzYXclMjBtb2Rlcm4lMjBhcmNoaXRlY3R1cmUlMjBvZmZpY2V8ZW58MHx8fHwxNzg5Mjg1OTYwfDA&ixlib=rb-4.1.0&q=85";

const lineReveal = {
  hidden: { y: "115%" },
  visible: (i) => ({
    y: 0,
    transition: { delay: 0.2 + i * 0.14, duration: 0.9, ease: [0.16, 1, 0.3, 1] },
  }),
};

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  visible: (d) => ({
    opacity: 1,
    y: 0,
    transition: { delay: d, duration: 0.7, ease: [0.16, 1, 0.3, 1] },
  }),
};

// Sizes each headline line so its natural width fills the block — equal-width lines without stretched word gaps.
function useFitTitle(lines) {
  const ref = useRef(null);
  const [sizes, setSizes] = useState(lines.map(() => "clamp(2rem, 6vw, 3.75rem)"));
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    const fit = () => {
      const width = el.clientWidth;
      if (!width) return;
      const canvas = document.createElement("canvas").getContext("2d");
      canvas.font = `800 100px ${getComputedStyle(el).fontFamily}`;
      setSizes(lines.map((l) => {
        const w = Math.max(canvas.measureText((l || "").toUpperCase()).width, 1);
        return `${Math.min(Math.max((width / w) * 100 * 0.995, 18), 110).toFixed(1)}px`;
      }));
    };
    fit();
    const ro = new ResizeObserver(fit);
    ro.observe(el);
    document.fonts?.ready?.then(fit);
    return () => ro.disconnect();
  }, [lines.join("|")]); // eslint-disable-line react-hooks/exhaustive-deps
  return [ref, sizes];
}

export default function Hero({ content }) {
  const { t, lang } = useT();
  const h =
    lang === "pl"
      ? {
          overline: content.hero_overline,
          title1: content.hero_title_1,
          title2: content.hero_title_2,
          subtitle: content.hero_subtitle,
        }
      : t.hero;
  const [titleRef, titleSizes] = useFitTitle([h.title1, h.title2]);

  const sectionRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: sectionRef, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 110]);

  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rotateX = useSpring(useTransform(my, [-0.5, 0.5], [5, -5]), { stiffness: 120, damping: 18 });
  const rotateY = useSpring(useTransform(mx, [-0.5, 0.5], [-5, 5]), { stiffness: 120, damping: 18 });

  const onMove = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };

  return (
    <section id="biuro-wirtualne-start" ref={sectionRef} data-testid="hero-section" className="relative pt-36 sm:pt-44 pb-16 sm:pb-24 overflow-hidden">
      <div aria-hidden="true" className="pointer-events-none absolute inset-0 -z-[1]">
        <div className="absolute -top-24 left-[8%] w-72 h-72 rounded-full bg-brand-yellow/25 dark:bg-brand-yellow/10 blur-3xl" />
        <div className="absolute top-40 right-[10%] w-80 h-80 rounded-full bg-brand-teal/20 dark:bg-brand-teal/15 blur-3xl" />
        <div className="absolute bottom-0 left-[38%] w-64 h-64 rounded-full bg-brand-red/15 dark:bg-brand-red/10 blur-3xl" />
      </div>
      <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-8 items-center">
        <div className="lg:col-span-7">
          <motion.p
            data-testid="hero-overline"
            custom={0}
            initial="hidden"
            animate="visible"
            variants={fadeUp}
            className="font-mono text-xs uppercase tracking-[0.25em] text-slate-600 dark:text-slate-400 font-semibold mb-6 flex items-center gap-2"
          >
            <MapPin size={14} className="text-brand-red" /> {h.overline}
          </motion.p>

          <h1 key={lang} ref={titleRef} data-testid="hero-title" style={{ fontFamily: '"Barlow Condensed", "Open Sans", sans-serif' }} className="font-extrabold uppercase tracking-tight leading-[0.95] w-full max-w-[640px]">
            <span className="block overflow-hidden pb-1" style={{ fontSize: titleSizes[0] }}>
              <motion.span custom={0} initial="hidden" animate="visible" variants={lineReveal} className="block whitespace-nowrap">
                {h.title1}
              </motion.span>
            </span>
            <span className="block overflow-hidden pb-2" style={{ fontSize: titleSizes[1] }}>
              <motion.span
                custom={1}
                initial="hidden"
                animate="visible"
                variants={lineReveal}
                className="block text-brand-teal whitespace-nowrap"
              >
                {h.title2}
              </motion.span>
            </span>
          </h1>

          <motion.p
            data-testid="hero-subtitle"
            custom={0.65}
            initial="hidden"
            animate="visible"
            variants={fadeUp}
            className="mt-6 max-w-xl text-base sm:text-lg text-slate-600 dark:text-slate-400 leading-relaxed"
          >
            {h.subtitle}
          </motion.p>

          <motion.div custom={0.8} initial="hidden" animate="visible" variants={fadeUp} className="mt-8 flex flex-wrap gap-4">
            <button
              data-testid="hero-primary-cta"
              onClick={() => scrollToSection("#biuro-wirtualne-cennik")}
              className="group bg-brand-teal hover:bg-brand-tealDark text-white font-semibold px-7 py-3.5 rounded-full text-sm transition-colors duration-200 inline-flex items-center gap-2"
            >
              {t.hero.cta1}
              <ArrowDownRight size={16} className="transition-transform duration-300 group-hover:rotate-45" />
            </button>
            <button
              data-testid="hero-secondary-cta"
              onClick={() => scrollToSection("#biuro-wirtualne-kontakt")}
              className="border border-slate-300 dark:border-slate-700 hover:border-brand-teal hover:text-brand-teal font-semibold px-7 py-3.5 rounded-full text-sm transition-colors duration-200"
            >
              {t.hero.cta2}
            </button>
          </motion.div>

          <motion.div
            data-testid="hero-metrics"
            custom={0.95}
            initial="hidden"
            animate="visible"
            variants={fadeUp}
            className="mt-12 grid grid-cols-2 lg:grid-cols-4 gap-x-6 gap-y-10 border-t border-slate-200 dark:border-slate-800 pt-8"
          >
            {t.hero.metrics.map((m, i) => (
              <div key={m.label} className={`border-t-[3px] pt-3 lg:-mt-8 ${["border-brand-teal", "border-brand-red", "border-brand-yellow", "border-brand-navy dark:border-slate-300"][i % 4]}`}>
                <div className="font-display font-bold text-xl sm:text-2xl lg:text-3xl tracking-tight leading-tight break-words">{m.value}</div>
                <div className="text-[11px] sm:text-sm text-slate-500 dark:text-slate-400 mt-1 leading-snug break-words hyphens-auto">{m.label}</div>
              </div>
            ))}
          </motion.div>
        </div>

        <motion.div
          initial={{ opacity: 0, scale: 0.96 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.5, duration: 1, ease: [0.16, 1, 0.3, 1] }}
          className="lg:col-span-5 [perspective:1200px]"
        >
          <motion.div
            data-testid="hero-image-frame"
            onMouseMove={onMove}
            onMouseLeave={() => {
              mx.set(0);
              my.set(0);
            }}
            style={{ rotateX, rotateY }}
            className="relative rounded-2xl overflow-hidden shadow-2xl shadow-slate-900/20 border border-slate-200 dark:border-slate-800"
          >
            <motion.img
              src={HERO_IMG}
              alt="Wirtualne biuro Warszawa Mokotów — siedziba Twoje Biuro, ul. Domaniewska 17/19"
              loading="eager"
              fetchPriority="high"
              decoding="async"
              style={{ y: imgY, scale: 1.15 }}
              className="w-full h-[420px] sm:h-[520px] object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-slate-900/50 via-transparent to-transparent" />
            <div className="absolute bottom-5 left-5 right-5 backdrop-blur-xl bg-white/10 border border-white/20 rounded-xl px-5 py-4 text-white">
              <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-slate-300">{t.hero.img_tag}</div>
              <div className="font-display font-bold text-lg mt-1">{t.hero.img_caption}</div>
            </div>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
}
